import { useCallback, useEffect, useState } from 'react'
import { getSiteThemes } from '@/lib/api'

type UseActiveThemeIdResult = {
  themeId: string | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const resolveThemeId = (data: any): string | null => {
  const activated = data?.activated_in_site
  if (activated?.id) return activated.id.toString()

  const themes = Array.isArray(data?.themes) ? data.themes : []
  if (themes.length > 0 && themes[0]?.id) return themes[0].id.toString()

  return null
}

export function useActiveThemeId(siteId?: string | null): UseActiveThemeIdResult {
  const [themeId, setThemeId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!siteId) return
    setIsLoading(true)
    setError(null)

    try {
      const data = await getSiteThemes(siteId)
      setThemeId(resolveThemeId(data))
    } catch (err: any) {
      setError(err?.message || 'Failed to load themes')
      setThemeId(null)
    } finally {
      setIsLoading(false)
    }
  }, [siteId])

  useEffect(() => {
    if (!siteId) return
    refresh()
  }, [siteId, refresh])

  return { themeId, isLoading, error, refresh }
}
